import { z } from 'zod';
import { getApiOrigin } from '../../shared/api-client';
import { getDurableItem, getPersistentItem, getPersistentJson, removeDurableItem, removePersistentItem, setDurableItem } from '../../vrs-auth/storage';
import { mobileLog } from './logging';
export const CALL_HISTORY_CACHE_KEY = 'vrs_call_history_scoped';
export const CALL_HISTORY_HIDDEN_KEY_PREFIX = 'vrs_call_history_hidden:';
const identifier = z.string().trim().min(1);
const ownerSchema = z.object({ origin: identifier, tenantId: z.string(), userId: identifier, role: z.enum(['client', 'interpreter']) });
const userSchema = z.object({ id: identifier.optional(), userId: identifier.optional(), tenantId: z.string().optional(),
    role: z.string().optional(), authenticatedAt: z.number().finite().optional() });
const authEnvelopeSchema = z.object({ token: z.string().min(1), userId: identifier.optional(),
    role: z.enum(['client', 'interpreter']).optional(), issuedAt: z.number().finite().optional() });
const recordSchema = z.object({
    id: identifier, contactName: identifier, phoneNumber: z.string().nullish().transform(value => value || ''),
    direction: z.enum(['outgoing', 'incoming', 'missed']), duration: z.number().finite().nonnegative(),
    timestamp: z.string().refine(value => Number.isFinite(Date.parse(value))), interpreterName: z.string().nullish().transform(value => value || undefined),
    callFlow: z.enum(['interpreted', 'point_to_point', 'vrs', 'vri', 'unknown']).optional(),
    chatLogStarred: z.boolean().optional(), chatMessageCount: z.number().int().nonnegative().optional(),
    hasChatLog: z.boolean().optional(), participantCount: z.number().int().nonnegative().optional()
});
const empty = { calls: [], incomplete: false };
let snapshot = null;
let revision = 0;
let writes = Promise.resolve(true);
const hiddenSchema = z.object({ version: z.literal(1), owner: ownerSchema,
    ids: z.array(z.string().trim().min(1).max(256)).max(10000),
    clearedThrough: z.number().int().nonnegative().max(8640000000000000).optional() });
let hiddenSnapshot = null;
let hiddenRevision = 0;
let hiddenWrites = Promise.resolve(true);
const ownerKey = (scope) => JSON.stringify(scope.owner);
export const callHistoryHiddenStorageKey = (scope) => `${CALL_HISTORY_HIDDEN_KEY_PREFIX}${encodeURIComponent(ownerKey(scope))}`;
function hiddenVisibility(scope) {
    return hiddenSnapshot?.ownerKey === ownerKey(scope) ? hiddenSnapshot.visibility : null;
}
function isVisible(call, visibility) {
    return !visibility.ids.has(call.id) && Date.parse(call.timestamp) > visibility.clearedThrough;
}
function filterSnapshot(scope, visibility) {
    if (!snapshot || JSON.stringify(snapshot.owner) !== ownerKey(scope)
        || !snapshot.history.calls.some(call => !isVisible(call, visibility)))
        return;
    snapshot = { ...snapshot, history: { ...snapshot.history,
            calls: snapshot.history.calls.filter(call => isVisible(call, visibility)) } };
}
function decodeVisibility(raw, scope) {
    if (raw === null)
        return { ids: new Set(), clearedThrough: 0 };
    const parsed = hiddenSchema.parse(JSON.parse(raw));
    if (JSON.stringify(parsed.owner) !== ownerKey(scope))
        throw new Error('History visibility owner mismatch');
    return { ids: new Set(parsed.ids), clearedThrough: parsed.clearedThrough ?? 0 };
}
/** Visibility preferences survive downloaded-cache cleanup, but belong to one account. */
export async function ensureCallHistoryVisibility(scope) {
    if (!isCurrentCallHistoryScope(scope))
        return false;
    if (hiddenVisibility(scope))
        return true;
    try {
        await hiddenWrites;
        if (!isCurrentCallHistoryScope(scope))
            return false;
        if (hiddenVisibility(scope))
            return true;
        const started = hiddenRevision;
        const visibility = decodeVisibility(await getDurableItem(callHistoryHiddenStorageKey(scope)), scope);
        if (!isCurrentCallHistoryScope(scope))
            return false;
        if (hiddenRevision !== started)
            return Boolean(hiddenVisibility(scope));
        hiddenSnapshot = { ownerKey: ownerKey(scope), visibility };
        filterSnapshot(scope, visibility);
        return true;
    }
    catch {
        mobileLog('warn', 'call_history_visibility_read_failed');
        return false;
    }
}
async function updateHistoryVisibility(scope, update) {
    if (!isCurrentCallHistoryScope(scope))
        return false;
    hiddenWrites = hiddenWrites.then(async () => {
        if (!isCurrentCallHistoryScope(scope))
            return false;
        // Read inside the serialized mutation: an earlier write may have
        // completed after its originating session ended or its receipt failed.
        const visibility = decodeVisibility(await getDurableItem(callHistoryHiddenStorageKey(scope)), scope);
        if (!isCurrentCallHistoryScope(scope))
            return false;
        update(visibility);
        const record = hiddenSchema.parse({ version: 1, owner: scope.owner, ids: [...visibility.ids],
            clearedThrough: visibility.clearedThrough });
        await setDurableItem(callHistoryHiddenStorageKey(scope), JSON.stringify(record));
        // A completed old-account write stays under that owner's key. It never
        // changes the new account's memory or confirms its obsolete action.
        hiddenRevision++;
        if (hiddenSnapshot?.ownerKey === ownerKey(scope))
            hiddenSnapshot = null;
        if (!isCurrentCallHistoryScope(scope))
            return false;
        hiddenSnapshot = { ownerKey: ownerKey(scope), visibility };
        revision++;
        filterSnapshot(scope, visibility);
        return true;
    }).catch(() => {
        mobileLog('warn', 'call_history_visibility_update_failed');
        return false;
    });
    return hiddenWrites;
}
export async function removeCallFromHistory(scope, callId) {
    return removeCallsFromHistory(scope, [callId]);
}
/** One durable write for a grouped Recent calls row: every id is hidden, or none is. */
export async function removeCallsFromHistory(scope, callIds) {
    const parsedIds = z.array(z.string().trim().min(1).max(256)).min(1).max(100).safeParse(callIds);
    if (!parsedIds.success)
        return false;
    return updateHistoryVisibility(scope, visibility => { parsedIds.data.forEach(id => visibility.ids.add(id)); });
}
/** Explicit user action; logout only clears the downloaded cache below.
 * A cutoff also hides older server pages that have never been downloaded.
 */
export function clearRecentCallHistory(scope) {
    const clearedThrough = Date.now();
    // Also hide already-displayed rows dated ahead of this device's clock,
    // without extending the cutoff over calls made after confirmation.
    const futureIds = readCallHistoryCache(scope).calls
        .filter(call => Date.parse(call.timestamp) > clearedThrough).map(call => call.id);
    return updateHistoryVisibility(scope, visibility => {
        visibility.clearedThrough = Math.max(visibility.clearedThrough, clearedThrough);
        futureIds.forEach(id => visibility.ids.add(id));
    });
}
export function currentCallHistoryScope() {
    const user = userSchema.safeParse(getPersistentJson('vrs_user_info'));
    if (!user.success || !(getPersistentItem('vrs_auth_token') || getPersistentItem('vrs_refresh_token')))
        return null;
    const parsed = ownerSchema.safeParse({ origin: getApiOrigin(), tenantId: user.data.tenantId || '',
        userId: user.data.id || user.data.userId, role: getPersistentItem('vrs_user_role') || user.data.role });
    if (!parsed.success)
        return null;
    const envelope = getPersistentJson('vrs_auth_token');
    let authenticatedAt = user.data.authenticatedAt ?? null;
    // Current native sign-ins preserve these identity fields through token
    // refresh. Profile responses must not replace the authenticated owner.
    // Legacy raw tokens have no envelope and retain the checked profile scope.
    if (envelope !== null && typeof envelope === 'object') {
        const auth = authEnvelopeSchema.safeParse(envelope);
        if (!auth.success || (auth.data.userId && auth.data.userId !== parsed.data.userId)
            || (auth.data.role && auth.data.role !== parsed.data.role))
            return null;
        authenticatedAt = auth.data.issuedAt ?? authenticatedAt;
    }
    return { owner: parsed.data, key: JSON.stringify([parsed.data, authenticatedAt]) };
}
export function isCurrentCallHistoryScope(scope) {
    return currentCallHistoryScope()?.key === scope.key;
}
export function readCallHistoryCache(scope = currentCallHistoryScope()) {
    if (!scope || !isCurrentCallHistoryScope(scope) || !snapshot || JSON.stringify(snapshot.owner) !== ownerKey(scope))
        return empty;
    const hidden = hiddenVisibility(scope);
    return hidden ? snapshot.history : { calls: [], incomplete: true };
}
export function callHistoryClearedThrough(scope) {
    return isCurrentCallHistoryScope(scope) ? hiddenVisibility(scope)?.clearedThrough ?? 0 : 0;
}
function decodeCache(raw, scope) {
    if (raw === null)
        return empty;
    let value;
    try {
        value = JSON.parse(raw);
    }
    catch {
        return { calls: [], incomplete: true };
    }
    const parsed = z.object({ version: z.literal(1), owner: ownerSchema, records: z.array(z.unknown()).max(100) }).safeParse(value);
    if (!parsed.success)
        return { calls: [], incomplete: true };
    if (JSON.stringify(parsed.data.owner) !== JSON.stringify(scope.owner))
        return empty;
    const calls = [], ids = new Set();
    let incomplete = false;
    for (const rawRecord of parsed.data.records) {
        const row = recordSchema.safeParse(rawRecord);
        if (!row.success || ids.has(row.data.id)) {
            incomplete = true;
            continue;
        }
        ids.add(row.data.id);
        calls.push(row.data);
    }
    return { calls, incomplete };
}
/** Reads never mutate the shared storage mirrors or overwrite a newer save/sign-out. */
export async function hydrateCallHistoryCache(scope = currentCallHistoryScope()) {
    if (!scope)
        return;
    const started = revision;
    try {
        if (!await ensureCallHistoryVisibility(scope))
            throw new Error('History visibility unavailable');
        // A queued save/removal must finish before reading its durable result.
        await writes;
        const raw = await getDurableItem(CALL_HISTORY_CACHE_KEY);
        if (revision !== started || !isCurrentCallHistoryScope(scope))
            return;
        snapshot = { owner: scope.owner, history: decodeCache(raw, scope) };
        const hidden = hiddenVisibility(scope);
        if (hidden)
            filterSnapshot(scope, hidden);
    }
    catch {
        if (revision === started && isCurrentCallHistoryScope(scope)) {
            snapshot = { owner: scope.owner, history: { ...readCallHistoryCache(scope), incomplete: true } };
            mobileLog('warn', 'call_history_cache_read_failed');
        }
    }
}
function persist(operation) {
    // Removal follows any already-started write, including when logout happens
    // while AsyncStorage is still saving. Tokens are never part of this cache.
    writes = writes.then(async () => {
        try {
            await operation();
            return true;
        }
        catch {
            mobileLog('warn', 'call_history_cache_write_failed');
            return false;
        }
    });
    return writes;
}
export function saveCallHistoryCache(scope, calls) {
    if (!isCurrentCallHistoryScope(scope))
        return Promise.resolve(false);
    const records = z.array(recordSchema).max(100).safeParse(calls);
    if (!records.success || new Set(records.data.map(row => row.id)).size !== records.data.length) {
        mobileLog('warn', 'call_history_cache_invalid_records');
        return Promise.resolve(false);
    }
    const hidden = hiddenVisibility(scope);
    if (!hidden)
        return ensureCallHistoryVisibility(scope).then(ready => ready ? saveCallHistoryCache(scope, calls) : false);
    revision++;
    const visible = records.data.filter(call => isVisible(call, hidden));
    snapshot = { owner: scope.owner, history: { calls: visible, incomplete: false } };
    const raw = JSON.stringify({ version: 1, owner: scope.owner, records: visible });
    return persist(() => setDurableItem(CALL_HISTORY_CACHE_KEY, raw));
}
export function appendCallHistoryCache(scope, call) {
    if (!hiddenVisibility(scope))
        return ensureCallHistoryVisibility(scope).then(ready => ready ? appendCallHistoryCache(scope, call) : false);
    return saveCallHistoryCache(scope, [call, ...readCallHistoryCache(scope).calls.filter(row => row.id !== call.id)].slice(0, 100));
}
export function clearCallHistoryCache() {
    revision++;
    hiddenRevision++;
    hiddenSnapshot = null;
    snapshot = null;
    removePersistentItem('vrs_call_history'); // Unowned legacy data is never adopted.
    return persist(() => removeDurableItem(CALL_HISTORY_CACHE_KEY));
}
